import "./quick_apply_flash.scss";
import { Link } from "../components/typography";
import CheckIcon from "../components/icons/check";
import ErrorIcon from "../components/icons/error";
import { useTranslation } from "react-i18next";
import { Flash } from "~/components/flash";
import { type CompletionStatus } from "./types";
import { useEffect } from "react";

export type AutofillStatus = "success" | "error";

type QuickApplyFlashProps = {
  autofillStatus: AutofillStatus;
  completionStatus: CompletionStatus | null;
  closeFlash: () => void;
  embedded?: boolean;
  autofillSource?: "seek" | "quick_apply" | null;
};

interface MessageProps {
  message: string;
  url: string;
  linkText: string | null;
  autofillSource?: "seek" | "quick_apply" | null;
}

interface StatusProps {
  autofillStatus: AutofillStatus;
  completionStatus: CompletionStatus | null;
}

function isConsideredComplete(completionStatus: CompletionStatus | null): boolean {
  if (!completionStatus) {
    return false;
  }

  return ["almost_finished", "complete"].includes(completionStatus);
}

const jobSeekersUrl = (path: string) => `${window.ENV.JOB_SEEKERS_URL}${path}`;
const jobSeekersProfileUrl = () => jobSeekersUrl("/profile");

const profileSourceUrl = (autofillSource?: "seek" | "quick_apply" | null): string => {
  if (autofillSource === "seek") {
    return "https://www.seek.com.au/profile/me";
  }

  return jobSeekersProfileUrl();
};

const Icon = ({ autofillStatus, completionStatus }: StatusProps) => {
  if (autofillStatus === "error" || completionStatus === "not_started") {
    return <ErrorIcon color="red" />;
  }

  return <CheckIcon color="green" />;
};

function useAutofillMessage(
  autofillStatus: AutofillStatus,
  completionStatus: CompletionStatus | null
): string {
  const { t } = useTranslation("job_post");

  if (autofillStatus === "error") {
    return t("application.quick_apply.error");
  }

  switch (completionStatus) {
    case "not_started":
      return t("application.quick_apply.not_started");
    case "in_progress":
      return t("application.quick_apply.in_progress");
    default:
      return t("application.quick_apply.success");
  }
}

function useAutofillLinkText(
  autofillStatus: AutofillStatus,
  completionStatus: CompletionStatus | null,
  autofillSource?: "seek" | "quick_apply" | null
): string | null {
  const { t } = useTranslation("job_post");

  if (autofillStatus === "error") {
    return null;
  }

  if (!!completionStatus && !isConsideredComplete(completionStatus)) {
    return t("application.quick_apply.my_greenhouse_profile");
  }

  return autofillSource === "seek" ? "SEEK" : "MyGreenhouse";
}

const Message = ({ message, linkText, url }: MessageProps) => {
  if (!linkText) {
    return <>{message}</>;
  }

  return (
    <>
      {message}{" "}
      <Link href={url} target="_blank">
        {linkText}
      </Link>
    </>
  );
};

export const QuickApplyFlash = ({
  autofillStatus,
  completionStatus: profileCompletionStatus,
  closeFlash,
  embedded,
  autofillSource,
}: QuickApplyFlashProps) => {
  // Completion status is only relevant for MyGreenhouse autofills
  const completionStatus = autofillSource === "seek" ? null : profileCompletionStatus;
  const editProfileLink = profileSourceUrl(autofillSource);
  const messageText = useAutofillMessage(autofillStatus, completionStatus);
  const linkText = useAutofillLinkText(autofillStatus, completionStatus, autofillSource);

  // Send postMessage to parent window when embedded and autofill status changes
  // Use useEffect to ensure we send the message with the latest values after state updates
  useEffect(() => {
    if (embedded && autofillStatus) {
      window.parent?.postMessage(
        {
          type: "autofill",
          autofillStatus,
          completionStatus,
          messageText,
          linkText,
          editProfileLink,
          autofillSource,
        },
        "*"
      );
    }
  }, [
    embedded,
    autofillStatus,
    completionStatus,
    messageText,
    linkText,
    editProfileLink,
    autofillSource,
  ]);

  if (embedded) {
    return null;
  }

  const backgroundColor = autofillStatus === "error" ? "extra-light-red" : "paler-gray";
  const showCloseIcon = autofillStatus !== "error";

  return (
    <Flash
      icon={<Icon autofillStatus={autofillStatus} completionStatus={completionStatus} />}
      backgroundColor={backgroundColor}
      message={<Message message={messageText} linkText={linkText} url={editProfileLink} />}
      showCloseIcon={showCloseIcon}
      closeFlash={closeFlash}
    />
  );
};
